// Italian dates and readable event names.
//
// The rule from §4.4, to be honoured everywhere: the date of an event is the
// date the fact appeared in the registry. The label is «rilevato nel RUI il
// ...», never «avvenuto il ...» -- companies report within ten working days and
// IVASS processes the paperwork in its own time, so the two can be months
// apart. The user-facing wording stays in Italian: this is an Italian registry.

import type { EventType, Role, SnapshotStatus } from './types'

export const DETECTED_LABEL = 'rilevato nel RUI il'

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const value = new Date(iso)
  return Number.isNaN(value.getTime()) ? '—' : value.toLocaleDateString('it-IT')
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const value = new Date(iso)
  return Number.isNaN(value.getTime())
    ? '—'
    : value.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
}

export function formatNumber(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('it-IT')
}

export function formatSigned(n: number): string {
  return n > 0 ? `+${formatNumber(n)}` : formatNumber(n)
}

/** User-facing names of the event types. Italian on purpose. */
export const EVENT_LABELS: Record<EventType, string> = {
  registration: 'Nuova iscrizione',
  deregistration: 'Cancellazione',
  detail_change: 'Variazione anagrafica',
  name_change: 'Cambio ragione sociale',
  inactivity_started: 'Inizio inoperativita',
  inactivity_ended: 'Fine inoperativita',
  mandate_added: 'Mandato nuovo',
  mandate_ended: 'Mandato cessato',
  office_added: 'Sede nuova',
  office_removed: 'Sede rimossa',
  website_added: 'Sito nuovo',
  website_removed: 'Sito rimosso',
  position_added: 'Carica nuova',
  position_removed: 'Carica rimossa',
  collaborator_added: 'Collaboratore nuovo',
  collaborator_removed: 'Collaboratore rimosso',
}

export function eventLabel(type: string): string {
  return EVENT_LABELS[type as EventType] ?? type
}

/**
 * Green for what comes in, red for what goes out, neutral for the rest. Spelled
 * out one type at a time rather than guessed from the name: the compiler then
 * refuses to forget an event type the day a new one is added.
 */
type Tone = 'in' | 'out' | 'neutral'

const EVENT_TONE: Record<EventType, Tone> = {
  registration: 'in',
  deregistration: 'out',
  detail_change: 'neutral',
  name_change: 'neutral',
  inactivity_started: 'out',
  inactivity_ended: 'in',
  mandate_added: 'in',
  mandate_ended: 'out',
  office_added: 'in',
  office_removed: 'out',
  website_added: 'in',
  website_removed: 'out',
  position_added: 'in',
  position_removed: 'out',
  collaborator_added: 'in',
  collaborator_removed: 'out',
}

const TONE_CLASS: Record<Tone, string> = {
  in: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  out: 'bg-rose-50 text-rose-800 ring-rose-600/20',
  neutral: 'bg-slate-100 text-slate-700 ring-slate-500/20',
}

export function eventBadgeClass(type: string): string {
  return TONE_CLASS[EVENT_TONE[type as EventType] ?? 'neutral']
}

/**
 * How an intermediary is shown: for natural persons only the registration
 * number (§4.7). Not a choice of the interface -- the API sends nothing else.
 */
export function intermediaryLabel(
  intermediary: { business_name: string | null; registration_number: string } | null,
): string {
  if (!intermediary) return '—'
  return intermediary.business_name ?? `${intermediary.registration_number} (persona fisica)`
}

/**
 * The API speaks English now, the dashboard still speaks Italian: these two
 * tables keep the enumerated values readable on screen without teaching the
 * user the wire vocabulary.
 */
export const ROLE_LABELS: Record<Role, string> = {
  reader: 'lettore',
  analyst: 'analista',
  admin: 'amministratore',
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role as Role] ?? role
}

export const SNAPSHOT_STATUS_LABELS: Record<SnapshotStatus, string> = {
  downloaded: 'scaricato',
  quarantined: 'quarantena',
  applied: 'applicato',
  unchanged: 'identico',
}

export function snapshotStatusLabel(status: string): string {
  return SNAPSHOT_STATUS_LABELS[status as SnapshotStatus] ?? status
}
